import { appIntl } from '@nxsys/helpers/Common';
import { PaginationType } from '@nxsys/types/models/Common';
import { showToast } from '@nxsys/utils/toast';
import { createSlice } from '@reduxjs/toolkit';
import { importActs } from '../actions';
import {
  GetAgencyUploadListParams,
  GetImportAgencyListParams,
  GetImportEmployeeListParams,
  GetImportProspectListParams,
  GetImportTimesheetListParams,
} from '../types';

export type ImportState = {
  importAgencyList: {
    loading: boolean;
    list: IImportAgency[];
    pagination: PaginationType;
    queryParams: GetImportAgencyListParams | null;
  };
  importEmployeeList: {
    loading: boolean;
    list: IImportEmployee[];
    pagination: PaginationType;
    queryParams: GetImportEmployeeListParams | null;
  };
  importProspectList: {
    loading: boolean;
    list: IImportProspect[];
    pagination: PaginationType;
    queryParams: GetImportProspectListParams | null;
  };
  importTimesheetList: {
    loading: boolean;
    list: IImportTimesheet[];
    pagination: PaginationType;
    queryParams: GetImportTimesheetListParams | null;
  };
  agencyUploadList: {
    loading: boolean;
    list: IAgencyUpload[];
    pagination: PaginationType;
    queryParams: GetAgencyUploadListParams | null;
  };
  agencyUploadSummary: {
    total: number;
    totalTimeSheet: number;
    totalEmployee: number;
    totalProspect: number;
  };
};

const initialState: ImportState = {
  importAgencyList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  importEmployeeList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  importProspectList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  importTimesheetList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  agencyUploadList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  agencyUploadSummary: {
    total: 0,
    totalTimeSheet: 0,
    totalEmployee: 0,
    totalProspect: 0,
  },
};

const importSlice = createSlice({
  name: 'import',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Get Import Agency List Records
    builder.addCase(importActs.getImportAgencyList.pending, (state) => {
      state.importAgencyList.loading = true;
    });
    builder.addCase(
      importActs.getImportAgencyList.fulfilled,
      (state, action) => {
        state.importAgencyList.loading = false;
        state.importAgencyList.list = action.payload.data;
        state.importAgencyList.pagination = action.payload.pagination;
        state.importAgencyList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(importActs.getImportAgencyList.rejected, (state) => {
      state.importAgencyList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'import.getImportAgencyListError',
        }),
      });
    });

    // Get Import Employee List Records
    builder.addCase(importActs.getImportEmployeeList.pending, (state) => {
      state.importEmployeeList.loading = true;
    });
    builder.addCase(
      importActs.getImportEmployeeList.fulfilled,
      (state, action) => {
        state.importEmployeeList.loading = false;
        state.importEmployeeList.list = action.payload.data;
        state.importEmployeeList.pagination = action.payload.pagination;
        state.importEmployeeList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(importActs.getImportEmployeeList.rejected, (state) => {
      state.importEmployeeList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'import.getImportEmployeeListError',
        }),
      });
    });

    // Get Import Prospect List Records
    builder.addCase(importActs.getImportProspectList.pending, (state) => {
      state.importProspectList.loading = true;
    });
    builder.addCase(
      importActs.getImportProspectList.fulfilled,
      (state, action) => {
        state.importProspectList.loading = false;
        state.importProspectList.list = action.payload.data;
        state.importProspectList.pagination = action.payload.pagination;
        state.importProspectList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(importActs.getImportProspectList.rejected, (state) => {
      state.importProspectList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'import.getImportProspectListError',
        }),
      });
    });

    // Get Agency Upload List Records
    builder.addCase(importActs.getAgencyUploadList.pending, (state) => {
      state.agencyUploadList.loading = true;
    });
    builder.addCase(
      importActs.getAgencyUploadList.fulfilled,
      (state, action) => {
        state.agencyUploadList.loading = false;
        state.agencyUploadList.list = action.payload.data;
        state.agencyUploadList.pagination = action.payload.pagination;
        state.agencyUploadList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(importActs.getAgencyUploadList.rejected, (state) => {
      state.agencyUploadList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'import.getAgencyUploadListError',
        }),
      });
    });

    // Get Import Timesheet List Records
    builder.addCase(importActs.getImportTimesheetList.pending, (state) => {
      state.importTimesheetList.loading = true;
    });
    builder.addCase(
      importActs.getImportTimesheetList.fulfilled,
      (state, action) => {
        state.importTimesheetList.loading = false;
        state.importTimesheetList.list = action.payload.data;
        state.importTimesheetList.pagination = action.payload.pagination;
        state.importTimesheetList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(importActs.getImportTimesheetList.rejected, (state) => {
      state.importTimesheetList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'import.getImportTimesheetListError',
        }),
      });
    });

    // Agency Upload Summary
    builder.addCase(
      importActs.getAgencyUploadSummary.fulfilled,
      (state, action) => {
        state.agencyUploadSummary = action.payload;
      },
    );
  },
});

export const importReducer = importSlice.reducer;
export const importAction = {
  ...importSlice.actions,
  ...importActs,
};
